import React, { useEffect, useRef, useState } from "react";
import { Form, Select, Button, Input, Row, Col, Table, message } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";

export type CareTeamDoctor = {
  key?: string;
  doctorName: string;
  doctorRegistrationId: string;
  doctorQualification: string;
  doctorMobileNo: string;
};

type CareTeamDetailsAyushmanProps = {
  form: any;
  portalData?: any;
  doctors?: any[];
  isDisabled?: boolean;
};

const qualificationOptions = [
  { label: "M.B.B.S.", value: "M.B.B.S." },
  { label: "M.D.", value: "M.D." },
  { label: "Master of Surgery (M.S.)", value: "Master of Surgery (M.S.)" },
  {
    label: "Doctorate of Medicine (D.M.)",
    value: "Doctorate of Medicine (D.M.)",
  },
  {
    label: "Master of Chirurgiae (M.Ch)",
    value: "Master of Chirurgiae (M.Ch)",
  },
  { label: "DNB", value: "DNB" },
  {
    label: "Master of Dental Surgery (M.D.S.)",
    value: "Master of Dental Surgery (M.D.S.)",
  },
  {
    label: "Diploma in Otorhinolaryngology",
    value: "Diploma in Otorhinolaryngology",
  },
  { label: "Ayush Doctor", value: "Ayush Doctor" },
  {
    label: "Registered Medical Assistant (RMA)",
    value: "Registered Medical Assistant (RMA)",
  },
  {
    label: "Diploma in Child Health (D.C.H)",
    value: "Diploma in Child Health (D.C.H)",
  },
  {
    label: "Bachelor of Dental Surgery",
    value: "Bachelor of Dental Surgery",
  },
];

const normalizeDoctorList = (list: any[] = []): CareTeamDoctor[] =>
  (list || [])
    .filter((d) => d?.doctorName)
    .map((d, index) => ({
      key: d?.key || `${d?.doctorRegistrationId || d?.doctorName}-${index}-${Date.now()}`,
      doctorName: d?.doctorName || "",
      doctorRegistrationId: d?.doctorRegistrationId || "",
      doctorQualification: d?.doctorQualification || "",
      doctorMobileNo: d?.doctorMobileNo || d?.doctorContact || "",
    }));

const CareTeamDetailsAyushman: React.FC<CareTeamDetailsAyushmanProps> = ({
  form,
  portalData,
  isDisabled,
}) => {
  // Local list is source of truth for table (form store alone was unreliable for multi-add)
  const [doctorList, setDoctorList] = useState<CareTeamDoctor[]>([]);
  const [draftComplete, setDraftComplete] = useState(false);
  const hydratedRef = useRef(false);

  const syncToForm = (list: CareTeamDoctor[]) => {
    const primary = list?.[0];
    setDoctorList(list);
    form?.setFieldsValue({
      doctorList: list,
      doctorName: primary?.doctorName,
      doctorRegistrationId: primary?.doctorRegistrationId,
      doctorQualification: primary?.doctorQualification,
      doctorMobileNo: primary?.doctorMobileNo,
      doctorContact: primary?.doctorMobileNo,
      selectedDoctor: "OTHER",
      // Mark care-team satisfied so full-form validate doesn't fail
      doctorListRequired: list.length > 0 ? "ok" : undefined,
    });
    // Clear leftover errors from empty draft fields after a successful add
    form?.setFields?.([
      { name: "draftDoctorName", errors: [] },
      { name: "draftDoctorRegistrationId", errors: [] },
      { name: "draftDoctorQualification", errors: [] },
      { name: "draftDoctorMobileNo", errors: [] },
      { name: "doctorListRequired", errors: [] },
    ]);
  };

  // Default Select Doctor = OTHER (mandatory)
  useEffect(() => {
    form?.setFieldsValue({ selectedDoctor: "OTHER" });
  }, [form]);

  // Hydrate once from portal / form initial values
  useEffect(() => {
    if (hydratedRef.current) return;

    const response = portalData?.casePreAuthOthersResponse;
    const fromForm = normalizeDoctorList(form?.getFieldValue?.("doctorList") || []);
    const fromList = normalizeDoctorList(response?.doctorList || []);

    if (fromForm.length > 0) {
      syncToForm(fromForm);
      hydratedRef.current = true;
      return;
    }

    if (fromList.length > 0) {
      syncToForm(fromList);
      hydratedRef.current = true;
      return;
    }

    if (response?.doctorName) {
      syncToForm([
        {
          key: "legacy-1",
          doctorName: response.doctorName,
          doctorRegistrationId: response.doctorRegistrationId || "",
          doctorQualification: response.doctorQualification || "",
          doctorMobileNo:
            response.doctorMobileNo || response.doctorContact || "",
        },
      ]);
      hydratedRef.current = true;
    }
  }, [portalData, form]);

  const handleDraftChange = () => {
    const values = form?.getFieldsValue?.(true) || {};
    const name = values?.draftDoctorName;
    const reg = values?.draftDoctorRegistrationId;
    const qual = values?.draftDoctorQualification;
    const contact = values?.draftDoctorMobileNo;
    const contactVal = contact == null ? "" : String(contact);
    setDraftComplete(!!name && !!reg && !!qual && contactVal.length === 10);
  };

  const clearDraftFields = () => {
    form?.setFieldsValue({
      draftDoctorName: undefined,
      draftDoctorRegistrationId: undefined,
      draftDoctorQualification: undefined,
      draftDoctorMobileNo: undefined,
      selectedDoctor: "OTHER",
    });
    setDraftComplete(false);
  };

  const handleAddDoctor = () => {
    const values = form?.getFieldsValue?.(true) || {};
    const newDoctor: CareTeamDoctor = {
      key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      doctorName: String(values.draftDoctorName || "").trim(),
      doctorRegistrationId: String(
        values.draftDoctorRegistrationId || "",
      ).trim(),
      doctorQualification: values.draftDoctorQualification,
      doctorMobileNo: String(values.draftDoctorMobileNo || "").trim(),
    };

    if (!newDoctor.doctorName) {
      message.error("Please enter doctor name");
      return;
    }
    if (!newDoctor.doctorRegistrationId) {
      message.error("Please enter registration ID");
      return;
    }
    if (!newDoctor.doctorQualification) {
      message.error("Please select qualification");
      return;
    }
    if (!/^[0-9]{10}$/.test(newDoctor.doctorMobileNo || "")) {
      message.error("Please enter valid 10-digit contact number");
      return;
    }

    const duplicate = doctorList.some(
      (d) =>
        d.doctorRegistrationId &&
        newDoctor.doctorRegistrationId &&
        d.doctorRegistrationId === newDoctor.doctorRegistrationId,
    );
    if (duplicate) {
      message.error("Doctor with this Registration ID is already added");
      return;
    }

    const nextList = [...doctorList, newDoctor];
    syncToForm(nextList);
    clearDraftFields();
    form?.setFieldsValue({ selectedDoctor: "OTHER" });
  };

  const handleDeleteDoctor = (key: string) => {
    const nextList = doctorList.filter((d) => d.key !== key);
    syncToForm(nextList);
    form?.setFieldsValue({ selectedDoctor: "OTHER" });
  };

  const columns = [
    {
      title: "Doctor Name",
      dataIndex: "doctorName",
      key: "doctorName",
    },
    {
      title: "Registration ID",
      dataIndex: "doctorRegistrationId",
      key: "doctorRegistrationId",
    },
    {
      title: "Qualification",
      dataIndex: "doctorQualification",
      key: "doctorQualification",
    },
    {
      title: "Contact",
      dataIndex: "doctorMobileNo",
      key: "doctorMobileNo",
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: CareTeamDoctor) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          disabled={isDisabled}
          onClick={() => handleDeleteDoctor(String(record.key))}
        />
      ),
    },
  ];

  return (
    <div style={{ marginBottom: "24px", marginTop: "14px" }}>
      <div
        style={{
          textDecoration: "underline",
          fontSize: "18px",
          marginBottom: "6px",
          fontWeight: "bold",
        }}
      >
        Care Team Details
      </div>

      {!isDisabled && (
        <>
          <Form.Item
            label="Select Doctor"
            name="selectedDoctor"
            initialValue="OTHER"
            rules={[{ required: true, message: "Please select an option" }]}
          >
            <Select
              placeholder="Select a doctor"
              style={{ width: "100%" }}
              disabled
            >
              <Select.Option value="OTHER">Other</Select.Option>
            </Select>
          </Form.Item>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Doctor Name" name="draftDoctorName">
                <Input
                  placeholder="Doctor Name"
                  onChange={handleDraftChange}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item
                label="Registration ID"
                name="draftDoctorRegistrationId"
              >
                <Input
                  placeholder="Registration ID"
                  onChange={handleDraftChange}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item
                label="Qualification"
                name="draftDoctorQualification"
              >
                <Select
                  options={qualificationOptions}
                  placeholder="Select qualification"
                  showSearch
                  allowClear
                  onChange={handleDraftChange}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Contact Number" name="draftDoctorMobileNo">
                <Input
                  placeholder="Contact Number"
                  maxLength={10}
                  onChange={handleDraftChange}
                />
              </Form.Item>
            </Col>
          </Row>

          <Button
            type="primary"
            onClick={handleAddDoctor}
            icon={<PlusOutlined />}
            disabled={!draftComplete}
            style={{ marginBottom: 20 }}
          >
            Add Doctor
          </Button>
        </>
      )}

      {doctorList.length > 0 && (
        <Table
          columns={columns}
          dataSource={doctorList}
          pagination={false}
          bordered
          rowKey={(record) => String(record.key)}
          className="claim-table"
          style={{ marginTop: 8 }}
          components={{
            header: {
              cell: (props: any) => (
                <th
                  {...props}
                  style={{
                    ...(props?.style || {}),
                    backgroundColor: "#004C8F",
                    color: "#fff",
                  }}
                />
              ),
            },
            body: {
              row: (props: any) => (
                <tr
                  {...props}
                  style={{
                    ...(props?.style || {}),
                    backgroundColor: "#E8F1FA",
                  }}
                />
              ),
            },
          }}
        />
      )}

      {!isDisabled && (
        <Form.Item
          name="doctorListRequired"
          rules={[
            {
              validator: async (_, value) => {
                const list =
                  form?.getFieldValue("doctorList") || doctorList || [];
                const hasDoctors =
                  value === "ok" ||
                  (Array.isArray(list) &&
                    list.some((d: any) => d?.doctorName));
                if (!hasDoctors) {
                  return Promise.reject(
                    new Error("Please add at least one doctor"),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
          style={{ marginBottom: 0 }}
        >
          <Input type="hidden" />
        </Form.Item>
      )}
    </div>
  );
};

export default CareTeamDetailsAyushman;
